/**
 * Formatting Utilities
 * Date, currency, and number formatters with Taiwan locale
 */

/**
 * Format Unix timestamp (ms) to Taiwan datetime string
 */
export function formatDate(timestamp: number, includeTime: boolean = true): string {
  const date = new Date(timestamp)

  if (includeTime) {
    return date.toLocaleString('zh-TW', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  }

  return date.toLocaleDateString('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/**
 * Format date as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 7) {
    return formatDate(timestamp, false)
  }

  if (days > 0) {
    return `${days} 天前`
  }

  if (hours > 0) {
    return `${hours} 小時前`
  }

  if (minutes > 0) {
    return `${minutes} 分鐘前`
  }

  return '剛剛'
}

/**
 * Format currency amount
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('zh-TW', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(amount) + ' ' + currency
}

/**
 * Format number with decimal places
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  return formatNumber(value, decimals) + '%'
}

/**
 * Format daily rate as percentage
 */
export function formatDailyRate(rate: number): string {
  return formatPercentage(rate * 100, 4)
}

/**
 * Format APR (annual percentage rate)
 */
export function formatAPR(dailyRate: number): string {
  const apr = dailyRate * 365 * 100
  return formatPercentage(apr, 2)
}

/**
 * Format rate for display (daily rate → APR %)
 * Alias for formatAPR for convenience
 */
export function formatRate(dailyRate: number): string {
  return formatAPR(dailyRate)
}

/**
 * Format Unix timestamp to Taiwan local time
 * Alias for formatDate for convenience
 */
export function formatTimestamp(timestamp: number, includeTime: boolean = true): string {
  return formatDate(timestamp, includeTime)
}

/**
 * Format duration in milliseconds to human readable
 */
export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) {
    return `${days} 天 ${hours % 24} 小時`
  }

  if (hours > 0) {
    return `${hours} 小時 ${minutes % 60} 分鐘`
  }

  if (minutes > 0) {
    return `${minutes} 分鐘`
  }

  return `${seconds} 秒`
}

/**
 * Format file size in bytes to human readable
 */
export function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`
}
